import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import Modal from "../../Components/Modal";
import Input from "../../Components/Input";
import OrderLine from "../../Components/OrderLine";
import TableBadge from "../../Components/TableBadge";
import { BackIcon, BagIcon, CheckIcon } from "../../Components/Icons";
import useMenuData from "../../hooks/useMenuData";
import Button from "../../Components/Button";
import Price from "../../Components/Price";
import DialogIntro from "../../Components/DialogIntro";
import { FIELD_CLASS, FIELD_ERROR_CLASS } from "../../Components/Input";
import { HiOutlineUser } from "react-icons/hi2";
import { useAppContext, emptyOrderState } from "../../context/AppContext";
import { getOrderSummary } from "../../util/helper";
import { useLocation } from "react-router-dom";
import {
  addOrderToDiningSession,
  verifyOpmAccessCode,
  validateToken,
  fetchDiningSessionOrderItems,
} from "../../services/ApiCalls";
import {
  saveAuthCookies,
  readScopedGuestDisplayName,
} from "../../util/cookies";
import {
  opmStorage,
  Keys,
  DEFAULT_TTL_SECONDS,
  GUEST_ELEMENT_SCOPE,
  clearStoredCart,
  tableScopeOf,
} from "../../util/opmStorage";

/**
 * The message a guest sees when an API call fails. The API's own error text is technical
 * and in English, so the guest always gets the app's message in their language.
 */
const pickApiErrorMessage = (err, fallback) => fallback;

/**
 * Splits the options a guest picked on a cart item into the extras the server charges for
 * (by option_value_id, looked up by the name shown in the item's own options) and the names
 * it could not match, which are sent as notes the way they were before.
 */
const resolveChosenOptions = (item) => {
  const idByName = new Map();
  for (const group of item?.options || []) {
    for (const value of group?.values || []) {
      if (value?.option_value_id && typeof value.name === "string") {
        idByName.set(value.name, value.option_value_id);
      }
    }
  }
  const extras = [];
  const unmatched = [];
  const seen = new Set();
  for (const name of [...(item?.cookingLevel || []), ...(item?.removeIngredients || [])]) {
    const id = idByName.get(name);
    if (id && !seen.has(id)) {
      seen.add(id);
      extras.push({ option_value_id: id });
    } else if (!id) {
      unmatched.push(name);
    }
  }
  return { extras, unmatched };
};

/** Inline error styled to match OPM (primary / surface). */
/** Map restaurant API order line → SwiperCard item shape (read-only / grey). */
const mapServerOrderItemToCard = (serverItem) => {
  const pn = serverItem.person_name;
  const trimmed =
    pn != null && String(pn).trim() !== "" ? String(pn).trim() : "";
  return {
    id: `server-${serverItem.id}`,
    translations: { name: serverItem.menu_item_name || "" },
    /** Only API `person_name`; empty → grouped under t("tableCenter"), never client-inferred. */
    name: trimmed,
    serverLine: true,
    quantity: serverItem.quantity ?? 1,
    price: serverItem.total_price ?? serverItem.price ?? 0,
    readOnly: true,
    isOrderSubmitted: false,
    options: [],
    cookingLevel: [],
    removeIngredients: [],
    serverNotes:
      Array.isArray(serverItem.note) && serverItem.note.length
        ? serverItem.note.join(", ")
        : "",
  };
};

/**
 * Group key: real person name, or server-only table-center label when API omitted person_name,
 * or local fallback when cart line has no name (never use table center for local-only rows).
 */
const getPersonGroupKey = (item, tableCenterLabel, localEmptyPersonLabel) => {
  const raw = item?.name;
  if (raw != null && String(raw).trim() !== "") return String(raw).trim();
  if (item.serverLine) return tableCenterLabel;
  const fb =
    localEmptyPersonLabel != null && String(localEmptyPersonLabel).trim() !== ""
      ? String(localEmptyPersonLabel).trim()
      : "";
  return fb;
};

/** Session rows first, then local cart; preserves flat indices for swipe/delete state. */
const buildGroupedOrderEntries = (
  sessionItemsForCard,
  localItems,
  tableCenterLabel,
  localEmptyPersonLabel
) => {
  const sc = sessionItemsForCard?.length ?? 0;
  const entries = [
    ...(sessionItemsForCard || []).map((item, i) => ({
      item,
      flatIndex: i,
    })),
    ...(localItems || []).map((item, j) => ({
      item,
      flatIndex: sc + j,
    })),
  ];
  const byKey = new Map();
  const keyOrder = [];
  for (const e of entries) {
    const key = getPersonGroupKey(
      e.item,
      tableCenterLabel,
      localEmptyPersonLabel
    );
    if (!byKey.has(key)) {
      byKey.set(key, []);
      keyOrder.push(key);
    }
    byKey.get(key).push(e);
  }
  return keyOrder.map((personLabel) => ({
    personLabel,
    entries: byKey.get(personLabel),
  }));
};

const InlineErrorBanner = ({ message }) => (
  <div
    role="alert"
    className="rounded-sm bg-danger-50 px-4 py-3 animate-fade-in"
  >
    <p className="text-sm font-medium text-danger text-center">
      {message}
    </p>
  </div>
);

const OrderListSection = React.memo(function OrderListSection({
  sessionItemsForCard = [],
  sessionFetchLoading = false,
  orderList,
  localEmptyPersonLabel,
  onChangeQuantity,
  onEdit,
  imageFor,
  t,
  listRef,
  navigate,
}) {
  const sessionCount = sessionItemsForCard?.length ?? 0;
  const groupedOrderBlocks = useMemo(
    () =>
      buildGroupedOrderEntries(
        sessionItemsForCard,
        orderList?.items,
        t("tableCenter"),
        localEmptyPersonLabel
      ),
    [sessionItemsForCard, orderList?.items, t, localEmptyPersonLabel]
  );

  return (
    <div className="flex flex-col gap-4" ref={listRef}>
      {sessionFetchLoading && sessionCount === 0 ? (
        <p className="text-sm text-muted text-center py-2">{t("loading")}</p>
      ) : null}
      {groupedOrderBlocks.map(({ personLabel, entries }, gi) => {
        const groupSubtotal = entries.reduce(
          (sum, { item }) => sum + (Number(item?.price) || 0),
          0
        );
        return (
          <section key={`person-${personLabel}-${gi}`} className="flex flex-col gap-2.5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-muted">{personLabel}</h2>
              <Price value={groupSubtotal} className="text-xs text-muted" />
            </div>
            {entries.map(({ item, flatIndex }) => (
              <OrderLine
                key={item.id || `f-${flatIndex}`}
                item={item}
                imageUrl={imageFor(item)}
                onChangeQuantity={onChangeQuantity}
                onEdit={onEdit}
              />
            ))}
          </section>
        );
      })}
      <button
        type="button"
        onClick={() => navigate("/")}
        className="h-12 rounded-lg border-[1.5px] border-dashed border-primary/40 text-primary text-sm font-bold flex items-center justify-center gap-2"
      >
        <span aria-hidden="true" className="text-lg">+</span>
        {t("Add dish")}
      </button>
    </div>
  );
});

const Index = ({
  closeModal,
  setSwiperShowDelete,
  isOrderSubmitted,
  t,
  orderList,
  getPrice,
  handleDeleteItem,
  swiperShowDelete,
  navigate,
  setOrderList,
  setCurrentOrderList,
  name,
  setName,
  setShowNameModal,
  setCookingLevel,
  setRemoveIngredients,
  handleChangeQuantity,
}) => {
  const menuData = useMenuData();
  const {
    authClaims,
    authToken,
    guestRestaurantId,
    setIsAuthenticated,
    setAuthClaims,
    setAuthToken,
    setGuestRestaurantId,
    language,
    checkoutOrder,
    setCheckoutOrder,
    diningSessionId,
    setDiningSessionId,
    storageElementId,
    setSelectedItem,
  } = useAppContext();

  const location = useLocation();

  const [sessionLineItems, setSessionLineItems] = useState([]);
  const [sessionFetchLoading, setSessionFetchLoading] = useState(false);
  /** Dining-session API root `total_amount` (server lines); local cart adds `order_summary.actual_amount`. */
  const [sessionBillTotalAmount, setSessionBillTotalAmount] = useState(0);

  const sessionItemsForCard = useMemo(
    () => sessionLineItems.map(mapServerOrderItemToCard),
    [sessionLineItems]
  );

  const localEmptyPersonLabel = useMemo(() => {
    const fromState =
      name != null && String(name).trim() !== "" ? String(name).trim() : "";
    if (fromState) return fromState;
    const fromStore = readScopedGuestDisplayName().trim();
    if (fromStore) return fromStore;
    return t("orderPersonSelf");
  }, [name, t]);

  const hasPendingPayment = useMemo(() => {
    if (!checkoutOrder?.items?.length) return false;
    const total = Number(checkoutOrder.order_summary?.total_amount ?? 0);
    const paid = Number(checkoutOrder.order_summary?.paid ?? 0);
    return total > paid;
  }, [checkoutOrder]);
  /** Dishes in the cart that have not gone to the kitchen yet. */
  const hasNewItems = (orderList?.items?.length ?? 0) > 0 && !isOrderSubmitted;
  const listRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGuestCheckoutModal, setShowGuestCheckoutModal] = useState(false);
  const [checkoutName, setCheckoutName] = useState("");
  const [checkoutCode, setCheckoutCode] = useState("");
  const [checkoutNameError, setCheckoutNameError] = useState(null);
  const [tableCodeError, setTableCodeError] = useState(null);
  const [orderFlowError, setOrderFlowError] = useState(null);

  useEffect(() => {
    if (location.pathname !== "/current-order") {
      setSessionLineItems([]);
      setSessionBillTotalAmount(0);
      setSessionFetchLoading(false);
      return;
    }
    const rid =
      authClaims?.restaurant_id != null
        ? String(authClaims.restaurant_id)
        : guestRestaurantId != null
          ? String(guestRestaurantId)
          : null;
    const e = storageElementId;
    if (!rid || !e) {
      setSessionLineItems([]);
      setSessionBillTotalAmount(0);
      setSessionFetchLoading(false);
      return;
    }
    let cancelled = false;
    setSessionFetchLoading(true);
    const lang = language === "en" ? "en" : "he";

    const tokenFromStorage = opmStorage.getWithTtl(Keys.token(rid, e));
    const effectiveToken = authToken || tokenFromStorage;

    const dsFromStorage = opmStorage.getWithTtl(Keys.diningSessionId(rid, e));
    const effectiveDs =
      diningSessionId != null && Number.isFinite(Number(diningSessionId))
        ? Number(diningSessionId)
        : dsFromStorage != null && dsFromStorage !== ""
          ? Number(dsFromStorage)
          : null;

    if (!effectiveToken || effectiveDs == null || !Number.isFinite(effectiveDs)) {
      setSessionLineItems([]);
      setSessionBillTotalAmount(0);
      setSessionFetchLoading(false);
      return;
    }

    fetchDiningSessionOrderItems(rid, effectiveDs, effectiveToken, lang)
      .then((data) => {
        if (cancelled) return;
        const raw = data?.items || [];
        setSessionLineItems(raw.filter((x) => !x.is_canceled));
        const ta = Number(data?.total_amount);
        setSessionBillTotalAmount(Number.isFinite(ta) ? ta : 0);
      })
      .catch(() => {
        if (cancelled) return;
        setSessionLineItems([]);
        setSessionBillTotalAmount(0);
      })
      .finally(() => {
        if (!cancelled) setSessionFetchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    location.key,
    authToken,
    authClaims?.restaurant_id,
    guestRestaurantId,
    storageElementId,
    diningSessionId,
    language,
  ]);

  useEffect(() => {
    const n =
      sessionItemsForCard.length + (orderList?.items?.length || 0);
    setSwiperShowDelete(Array.from({ length: n }, () => false));
  }, [
    sessionItemsForCard.length,
    orderList?.items?.length,
    setSwiperShowDelete,
  ]);

  const buildOrderPayload = useCallback(
    (personName) => {
      const items = orderList?.items
        ?.filter((item) => !item.isOrderSubmitted)
        ?.map((item) => {
          const { extras, unmatched } = resolveChosenOptions(item);
          const note = [...unmatched];
          if (item.notes) {
            note.push(item.notes);
          }

          return {
            menu_item_id: item.menu_item_id,
            person_name: item.name || personName,
            quantity: item.quantity || 1,
            extras,
            note,
          };
        });

      return {
        items,
      };
    },
    [orderList]
  );

  const runSubmitOrder = useCallback(
    async (personName, token, restaurantId, elementId, tokenDiningSessionId) => {
      const payload = buildOrderPayload(personName);
      const r = String(restaurantId);
      const el = String(elementId);
      // The OPM token belongs to one dining session: the table the waiter opened. Its id
      // in the token wins; the stored id is only a fallback for an older token.
      const dsFromStorage = opmStorage.getWithTtl(Keys.diningSessionId(r, el));
      const candidates = [tokenDiningSessionId, diningSessionId, dsFromStorage];
      const effectiveDiningSessionId = candidates
        .map((v) => (v != null && v !== "" ? Number(v) : NaN))
        .find((v) => Number.isInteger(v) && v > 0);
      if (effectiveDiningSessionId == null) {
        throw new Error("No open table for this OPM token");
      }
      const response = await addOrderToDiningSession(
        restaurantId,
        effectiveDiningSessionId,
        payload,
        token,
        language === "en" ? "en" : "he"
      );
      const dsRaw = response?.dining_session_id;
      const dsId =
        dsRaw != null && dsRaw !== "" ? Number(dsRaw) : null;
      if (dsId != null && Number.isFinite(dsId)) {
        opmStorage.setWithTtl(
          Keys.diningSessionId(r, el),
          dsId,
          DEFAULT_TTL_SECONDS
        );
        setDiningSessionId(dsId);
      }
      const updatedItems = orderList.items.map((od) => ({
        ...od,
        isOrderSubmitted: true,
        name: od?.isOrderSubmitted ? od?.name : personName,
      }));
      setCheckoutOrder({
        ...orderList,
        items: updatedItems,
        order_summary: getOrderSummary(updatedItems, orderList.order_summary),
      });
      // Entering the table code copied the cart into the table's storage before it was
      // sent. Drop that copy too, or the app reloads the sent dishes as an unsent cart
      // once it switches to the table and sends them again.
      clearStoredCart(r, [el, GUEST_ELEMENT_SCOPE]);
      setOrderList(emptyOrderState());
      setCurrentOrderList(emptyOrderState());
      navigate("/order-submit");
    },
    [
      buildOrderPayload,
      navigate,
      orderList,
      setCheckoutOrder,
      setOrderList,
      setCurrentOrderList,
      setDiningSessionId,
      diningSessionId,
      language,
    ]
  );

  const handleModalSubmit = useCallback(async () => {
    setSwiperShowDelete([]);
    // The page is for ordering: it sends new dishes to the kitchen, and with nothing new to
    // send it takes the guest back to the menu. Paying has its own tab.
    if (!hasNewItems) {
      closeModal();
      return;
    }
    if (!authToken) {
      if (!guestRestaurantId) {
        setOrderFlowError(t("errorMissingRestaurantForOrder"));
        return;
      }
      setCheckoutName(name || orderList?.items[0]?.name || "");
      setCheckoutCode("");
      setCheckoutNameError(null);
      setTableCodeError(null);
      setShowGuestCheckoutModal(true);
      return;
    }

    if (name || orderList?.items[0]?.name) {
      const personName = name || orderList?.items[0]?.name;
      setIsSubmitting(true);
      setOrderFlowError(null);
      try {
        await runSubmitOrder(
          personName,
          authToken,
          authClaims?.restaurant_id,
          tableScopeOf(authClaims),
          authClaims?.dining_session_id
        );
      } catch (err) {
        console.error("Failed to submit order:", err);
        setOrderFlowError(
          pickApiErrorMessage(err, t("orderSubmitFailed"))
        );
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setShowNameModal(true);
    }
  }, [
    setSwiperShowDelete,
    hasNewItems,
    orderList,
    name,
    setShowNameModal,
    closeModal,
    authToken,
    authClaims,
    guestRestaurantId,
    t,
    runSubmitOrder,
  ]);

  const handleGuestCheckoutConfirm = useCallback(async () => {
    const personName = checkoutName.trim();
    if (!personName) {
      setCheckoutNameError(t("nameRequired"));
      return;
    }
    setCheckoutNameError(null);

    const code = checkoutCode.replace(/\D/g, "").slice(0, 4);
    if (code.length !== 4) {
      setTableCodeError(t("tableCodeInvalid"));
      return;
    }

    if (!guestRestaurantId) {
      setShowGuestCheckoutModal(false);
      return;
    }

    setIsSubmitting(true);
    setTableCodeError(null);
    try {
      const { token } = await verifyOpmAccessCode(guestRestaurantId, code);
      const claims = await validateToken(token);
      saveAuthCookies(token, claims);
      opmStorage.setWithTtl(
        Keys.guestName(claims.restaurant_id, tableScopeOf(claims)),
        personName,
        DEFAULT_TTL_SECONDS
      );
      setName(personName);
      setIsAuthenticated(true);
      setAuthClaims(claims);
      setAuthToken(token);
      setGuestRestaurantId(claims.restaurant_id);
      setShowGuestCheckoutModal(false);
      setCheckoutCode("");
      setCheckoutName("");
      setTableCodeError(null);
      try {
        await runSubmitOrder(
          personName,
          token,
          claims.restaurant_id,
          tableScopeOf(claims),
          claims.dining_session_id
        );
      } catch (submitErr) {
        console.error("Submit after table code failed:", submitErr);
        setOrderFlowError(
          pickApiErrorMessage(submitErr, t("orderSubmitFailed"))
        );
      }
    } catch (err) {
      console.error("Table code verification failed:", err);
      setTableCodeError(
        pickApiErrorMessage(err, t("tableCodeVerificationFailed"))
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    checkoutName,
    checkoutCode,
    guestRestaurantId,
    t,
    runSubmitOrder,
    setIsAuthenticated,
    setAuthClaims,
    setAuthToken,
    setGuestRestaurantId,
    setName,
  ]);

  // Server lines carry no photo: find the dish's photo on the menu by id or name.
  const imageFor = useCallback(
    (item) => {
      if (item?.image_url) return item.image_url;
      const itemName = item?.translations?.name;
      for (const cat of menuData?.categories || []) {
        for (const dish of cat?.items || []) {
          if (
            (item?.menu_item_id != null && dish.menu_item_id === item.menu_item_id) ||
            (itemName && dish?.translations?.name === itemName)
          ) {
            return dish.image_url || null;
          }
        }
      }
      return null;
    },
    [menuData]
  );

  const editItem = useCallback(
    (item) => {
      const itemName = item?.translations?.name || item?.id;
      navigate(`/customize/${itemName}`, { state: { ...item, edit: true } });
      setSelectedItem({ ...item, edit: true });
      setCookingLevel([...(item.cookingLevel || [])]);
      setRemoveIngredients([...(item.removeIngredients || [])]);
    },
    [navigate, setSelectedItem, setCookingLevel, setRemoveIngredients]
  );

  const tableTotal =
    (Number(sessionBillTotalAmount) || 0) +
    (Number(orderList?.order_summary?.actual_amount) || 0);

  const orderListSection = useMemo(() => {
    const hasLocal = (orderList?.items?.length ?? 0) > 0;
    const hasSession = sessionItemsForCard.length > 0;
    const showList = hasLocal || hasSession || sessionFetchLoading;

    if (showList) {
      return (
        <OrderListSection
          sessionItemsForCard={sessionItemsForCard}
          sessionFetchLoading={sessionFetchLoading}
          orderList={orderList}
          localEmptyPersonLabel={localEmptyPersonLabel}
          onChangeQuantity={handleChangeQuantity}
          onEdit={editItem}
          imageFor={imageFor}
          t={t}
          listRef={listRef}
          navigate={navigate}
        />
      );
    }
    if (hasPendingPayment && !sessionFetchLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-16 gap-3 px-4">
          <div className="w-14 h-14 rounded-full bg-success-50 text-success flex items-center justify-center">
            <CheckIcon size={28} />
          </div>
          <p className="text-lg font-bold text-dark text-center">{t("submitTitle")}</p>
          <p className="text-muted text-sm text-center">{t("submitDescription")}</p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="w-14 h-14 rounded-full bg-secondary text-subtle flex items-center justify-center">
          <BagIcon size={26} />
        </div>
        <p className="text-muted text-sm">{t("emptyOrder")}</p>
        <Button variant="brand" onClick={() => navigate("/")}>
          {t("Back to Menu")}
        </Button>
      </div>
    );
  }, [
    orderList,
    sessionItemsForCard,
    sessionFetchLoading,
    localEmptyPersonLabel,
    handleChangeQuantity,
    editItem,
    imageFor,
    t,
    navigate,
    hasPendingPayment,
  ]);

  const hasAnything =
    (orderList?.items?.length ?? 0) > 0 || sessionItemsForCard.length > 0 || hasPendingPayment;


  return (
    <>
      <div className="h-dvh flex flex-col bg-background">
        <header className="flex items-center gap-1 px-3 pt-2">
          <button
            type="button"
            onClick={() => {
              navigate("/");
              setOrderFlowError(null);
            }}
            aria-label={t("Back to Menu")}
            className="w-11 h-11 flex items-center justify-center text-dark"
          >
            <BackIcon />
          </button>
          <h1 className="flex-1 text-lg font-bold text-dark">{t("Current Order")}</h1>
          <TableBadge />
        </header>

        <main className="flex-1 overflow-y-auto scrollbar-hide px-5 pt-3 pb-6">
          {orderFlowError ? (
            <div className="mb-3 flex flex-col gap-1">
              <InlineErrorBanner message={orderFlowError} />
              <Button variant="ghost" fullWidth onClick={() => setOrderFlowError(null)}>
                {t("dismissError")}
              </Button>
            </div>
          ) : null}
          {orderListSection}
        </main>

        {hasAnything && (
          <div
            className="flex-none bg-background rounded-t-xl shadow-sheet px-6 pt-4 flex flex-col gap-3.5"
            style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))" }}
          >
            <div className="flex justify-between text-base font-bold text-dark">
              <span>{t("tableTotal")}</span>
              <Price value={tableTotal} />
            </div>
            <Button size="lg" fullWidth disabled={isSubmitting} onClick={handleModalSubmit}>
              {hasNewItems ? t("Save & Order") : t("addMoreDishes")}
            </Button>
          </div>
        )}
      </div>

      {showGuestCheckoutModal ? (
        <Modal
          onClose={() => {
            setShowGuestCheckoutModal(false);
            setCheckoutName("");
            setCheckoutCode("");
            setTableCodeError(null);
            setCheckoutNameError(null);
          }}
          overlayClassName="!z-[60]"
          isCloseIcon={false}
          isFullscreen={false}
          isBottomButton={false}
          className="p-6 w-full max-w-md m-6 bg-surface"
          isHeader={false}
        >
          <div className="flex flex-col gap-4">
            <DialogIntro
              icon={HiOutlineUser}
              title={t("guestCheckoutTitle")}
              description={t("guestCheckoutSubtitle")}
            />

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="guest-checkout-name"
                className="text-xs font-medium text-muted"
              >
                {t("Your Name")}
              </label>
              <Input
                id="guest-checkout-name"
                name="checkoutName"
                type="text"
                autoComplete="name"
                className={`${FIELD_CLASS} ${checkoutNameError ? FIELD_ERROR_CLASS : ""}`}
                placeholder={t("Your Name")}
                value={checkoutName}
                onChange={(e) => {
                  setCheckoutNameError(null);
                  setCheckoutName(e.target.value);
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="guest-checkout-code"
                className="text-xs font-medium text-muted"
              >
                {t("tableCodeTitle")}
              </label>
              <Input
                id="guest-checkout-code"
                name="checkoutCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                dir="ltr"
                className={`${FIELD_CLASS} text-center text-xl font-bold tracking-widest tabular-nums ${tableCodeError ? FIELD_ERROR_CLASS : ""}`}
                placeholder={t("tableCodePlaceholder")}
                value={checkoutCode}
                onChange={(e) => {
                  setTableCodeError(null);
                  setCheckoutCode(
                    e.target.value.replace(/\D/g, "").slice(0, 4)
                  );
                }}
              />
            </div>

            {checkoutNameError ? (
              <InlineErrorBanner message={checkoutNameError} />
            ) : null}
            {tableCodeError ? (
              <InlineErrorBanner message={tableCodeError} />
            ) : null}

            <div className="flex flex-col gap-2 pt-1">
              <Button
                size="lg"
                fullWidth
                disabled={isSubmitting}
                onClick={handleGuestCheckoutConfirm}
              >
                {t("Confirm")}
              </Button>
              <Button
                variant="secondary"
                fullWidth
                disabled={isSubmitting}
                onClick={() => {
                  setShowGuestCheckoutModal(false);
                  setCheckoutName("");
                  setCheckoutCode("");
                  setTableCodeError(null);
                  setCheckoutNameError(null);
                }}
              >
                {t("Cancel")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default Index;
